import { ScissorsIcon, TrashIcon } from '@heroicons/react/24/outline'
import '@tailwindplus/elements'
import classNames from 'classnames'
import React, { useEffect, useRef } from 'react'
import { Link } from 'react-router'
import { useSortable } from '@dnd-kit/react/sortable'
import { t } from 'shared'
import { ImageItem } from './types'

let lastPopoverDismissedAt = 0

const SortableImage = ({
  image,
  index,
  imagesCount,
  handleDelete,
  getCropUrl,
}: {
  image: ImageItem
  index: number
  imagesCount: number
  handleDelete: (index: number) => void
  getCropUrl?: (index: number) => string | undefined
}) => {
  const { ref: sortableRef, isDragSource, isDropTarget } = useSortable({ id: image.signedId, index })
  const imageRef = useRef<HTMLDivElement | null>(null)
  const popoverRef = useRef<HTMLElement | null>(null)
  const popoverId = useRef(`image-upload-popover-${Math.random().toString(36).slice(2)}`).current
  const pressStart = useRef(0)
  const startPos = useRef({ x: 0, y: 0 })
  const didMove = useRef(false)
  const cropUrl = getCropUrl?.(index)
  const canDelete = imagesCount > 1

  const setRef = (el: HTMLDivElement | null) => {
    imageRef.current = el
    sortableRef(el)
  }

  useEffect(() => {
    const popover = popoverRef.current
    if (!popover) return
    const onToggle = (e: Event) => {
      if ((e as ToggleEvent).newState === 'closed') lastPopoverDismissedAt = Date.now()
    }
    popover.addEventListener('toggle', onToggle)
    return () => popover.removeEventListener('toggle', onToggle)
  }, [])

  const showContextMenu = () => {
    const image = imageRef.current
    const popover = popoverRef.current
    if (!image || !popover) return
    const rect = image.getBoundingClientRect()
    popover.style.top = `${rect.bottom + 4}px`
    popover.style.left = `${rect.left}px`
    popover.showPopover()
  }

  const handlePointerDown = (e: React.PointerEvent) => {
    didMove.current = false
    startPos.current = { x: e.clientX, y: e.clientY }
    pressStart.current = e.timeStamp
  }

  const handlePointerMove = (e: React.PointerEvent) => {
    if (Math.abs(e.clientX - startPos.current.x) > 5 || Math.abs(e.clientY - startPos.current.y) > 5) {
      didMove.current = true
    }
  }

  const handlePointerUp = (e: React.PointerEvent) => {
    if (e.pointerType !== 'touch' && e.pointerType !== 'pen') return
    if (didMove.current) return
    if (e.timeStamp - pressStart.current >= 250) return
    if (Date.now() - lastPopoverDismissedAt < 300) return
    showContextMenu()
  }

  return (
    <div
      ref={setRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onContextMenu={(e) => e.preventDefault()}
      style={{ WebkitTouchCallout: 'none' } as React.CSSProperties}
      className={classNames(
        'relative w-[130px] aspect-3/2 rounded-xl overflow-hidden cursor-grab border-2 shrink-0 transition-[border-color,opacity] duration-100 group active:cursor-grabbing touch-none',
        isDropTarget && !isDragSource ? 'border-indigo-500' : 'border-transparent',
        isDragSource && 'opacity-75'
      )}
    >
      <img src={image.url} alt="" className="w-full h-full object-contain bg-gray-100" />
      {index === 0 && (
        <span className="absolute bottom-[0.4rem] left-[0.4rem] bg-me-purple-600 text-white text-[0.7rem] font-bold py-[0.2rem] px-[0.45rem] rounded">
          {t('image_upload.cover_image')}
        </span>
      )}
      <div className="absolute top-1.5 right-1.5 flex flex-col gap-1 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity duration-150">
        {cropUrl && (
          <Link
            to={cropUrl}
            onPointerDown={(e) => e.stopPropagation()}
            className="bg-black/50 hover:bg-black/75 text-white rounded-md p-1.5 flex items-center justify-center"
          >
            <ScissorsIcon className="w-4 h-4" />
          </Link>
        )}
        {canDelete && (
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => handleDelete(index)}
            className="bg-black/50 hover:bg-red-600/90 text-white rounded-md p-1.5 flex items-center justify-center cursor-pointer"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        )}
      </div>
      <el-popover
        ref={popoverRef as React.Ref<HTMLElement>}
        id={popoverId}
        popover="auto"
        onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
        className="fixed m-0 p-1 rounded-lg shadow-lg border border-gray-200 min-w-[160px] flex flex-col"
        style={{ backgroundColor: '#282828' }}
      >
        {cropUrl && (
          <Link
            to={cropUrl}
            onClick={() => popoverRef.current?.hidePopover()}
            className="flex items-center gap-2 px-3 py-2 text-sm !text-me-purple-300 hover:bg-gray-100 rounded-md"
          >
            <ScissorsIcon className="w-4 h-4" />
            {t('verbs.crop')}
          </Link>
        )}
        {canDelete && (
          <button
            type="button"
            onClick={() => {
              popoverRef.current?.hidePopover()
              handleDelete(index)
            }}
            className="flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-gray-100 rounded-md cursor-pointer"
          >
            <TrashIcon className="w-4 h-4" />
            {t('verbs.delete')}
          </button>
        )}
      </el-popover>
    </div>
  )
}

export default SortableImage
